import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextField, Autocomplete, CircularProgress, Box, Typography } from '@mui/material';
import axios from 'axios';
import domain from '../../util/domain';

const DEFAULT_LIMIT = 50;
const MIN_SEARCH_CHARS = 2;
const DEBOUNCE_DELAY = 300;
const LOAD_MORE_OPTION = { __meta: 'loadMore', label: 'Show more results…' };

const formatOptionLabel = (option) => {
    if (!option) {
        return '';
    }

    const name = option.college_name || '';
    const code = option.college_code ? ` (${option.college_code})` : '';

    return `${name}${code}`;
};

const sanitizeField = (value) => {
    if (!value) return '';
    const text = String(value).trim();
    return text === '' ? '' : text;
};

const SearchCollege = ({
    defaultOption,
    college,
    onCollegeChange,
    limit = DEFAULT_LIMIT,
    minSearchLength = MIN_SEARCH_CHARS,
    label = 'Select a College',
    placeholder = 'Search by college name, code, address or website',
    autoFocus = false,
    disabled = false,
    textFieldProps = {}
}) => {
    const initialOption = college || defaultOption || null;

    const [searchInput, setSearchInput] = useState(initialOption ? formatOptionLabel(initialOption) : '');
    const [options, setOptions] = useState(initialOption ? [initialOption] : []);
    const [selectedOption, setSelectedOption] = useState(initialOption);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);

    const fetchIdRef = useRef(0);

    useEffect(() => {
        if (initialOption) {
            setSelectedOption(initialOption);
            setOptions([initialOption]);
            setSearchInput(formatOptionLabel(initialOption));
        }
    }, [initialOption]);

    const normalizedQuery = useMemo(
        () => searchInput.trim().toUpperCase(),
        [searchInput]
    );

    useEffect(() => {
        if (!normalizedQuery || normalizedQuery.length < minSearchLength) {
            setHasMore(false);
            if (page === 0 && !normalizedQuery) {
                setOptions(initialOption ? [initialOption] : []);
            } else if (page === 0) {
                setOptions([]);
            }
            setLoading(false);
            return;
        }

        const currentFetchId = ++fetchIdRef.current;
        const controller = new AbortController();

        setLoading(true);

        const timeoutId = setTimeout(async () => {
            try {
                const response = await axios.get(`${domain}/college/search`, {
                    params: {
                        q: normalizedQuery,
                        limit,
                        offset: page * limit,
                    },
                    signal: controller.signal,
                });

                if (fetchIdRef.current !== currentFetchId) {
                    return;
                }

                const { results = [], hasMore: more = false } = response.data || {};

                setHasMore(more);
                setOptions((prev) => {
                    const base = page === 0 ? [] : prev.filter((option) => !option.__meta);
                    const uniqueOptions = new Map(base.map((option) => [option.id, option]));
                    results.forEach((option) => {
                        uniqueOptions.set(option.id, option);
                    });
                    return Array.from(uniqueOptions.values());
                });
            } catch (error) {
                if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') {
                    return;
                }
                console.error('Error fetching colleges:', error);
                if (fetchIdRef.current === currentFetchId) {
                    setHasMore(false);
                    if (page === 0) {
                        setOptions([]);
                    }
                }
            } finally {
                if (fetchIdRef.current === currentFetchId) {
                    setLoading(false);
                }
            }
        }, DEBOUNCE_DELAY);

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [normalizedQuery, limit, page, minSearchLength, initialOption]);

    const displayedOptions = useMemo(() => {
        return hasMore ? [...options, LOAD_MORE_OPTION] : options;
    }, [options, hasMore]);

    const handleInputChange = (event, newInputValue, reason) => {
        if (reason === 'reset') {
            if (newInputValue === LOAD_MORE_OPTION.label) {
                return;
            }
            setSearchInput(newInputValue || '');
            return;
        }

        if (reason === 'clear') {
            setSearchInput('');
            setOptions(initialOption ? [initialOption] : []);
            setSelectedOption(null);
            setHasMore(false);
            setPage(0);
            if (onCollegeChange) {
                onCollegeChange(null);
            }
            return;
        }

        if (reason === 'input') {
            if (selectedOption) {
                setSelectedOption(null);
            }
            setSearchInput(newInputValue || '');
            setPage(0);
            setHasMore(false);
            setOptions([]);
        }
    };

    const handleOptionChange = (event, newValue) => {
        if (newValue && newValue.__meta === LOAD_MORE_OPTION.__meta) {
            event?.preventDefault?.();
            setPage((prev) => prev + 1);
            return;
        }

        setSelectedOption(newValue || null);

        if (newValue) {
            setSearchInput(formatOptionLabel(newValue));
        }

        if (onCollegeChange) {
            onCollegeChange(newValue || null);
        }
    };

    const getOptionLabel = (option) => {
        if (!option) {
            return '';
        }
        if (option.__meta === LOAD_MORE_OPTION.__meta) {
            return option.label;
        }
        return formatOptionLabel(option);
    };

    const isOptionEqualToValue = (option, value) => {
        if (option?.__meta || value?.__meta) {
            return option?.__meta === value?.__meta;
        }
        return option?.id === value?.id;
    };

    return (
        <Box sx={{ margin: '0 auto', width: '100%' }}>
            <Autocomplete
                autoComplete
                fullWidth
                filterOptions={(x) => x}
                options={displayedOptions}
                inputValue={searchInput}
                onInputChange={handleInputChange}
                onChange={handleOptionChange}
                value={selectedOption || null}
                loading={loading}
                disabled={disabled}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                ListboxProps={{ sx: { maxHeight: 320 } }}
                renderOption={(props, option) => {
                    if (option.__meta === LOAD_MORE_OPTION.__meta) {
                        return (
                            <li {...props} style={{ fontStyle: 'italic' }}>
                                {option.label}
                            </li>
                        );
                    }
                    return (
                        <li {...props}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#eb7a24' }}
                                >
                                    {formatOptionLabel(option)}
                                </Typography>
                                {sanitizeField(option.univerisity_name) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span role="img" aria-label="university">🎓</span>
                                        <span>{sanitizeField(option.univerisity_name)}</span>
                                    </Typography>
                                )}
                                {sanitizeField(option.college_address) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span role="img" aria-label="address">📍</span>
                                        <span>{sanitizeField(option.college_address)}</span>
                                    </Typography>
                                )}
                                {sanitizeField(option.college_website) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span role="img" aria-label="website">🌐</span>
                                        <span>{sanitizeField(option.college_website)}</span>
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder}
                        variant="outlined"
                        autoFocus={autoFocus}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        {...textFieldProps}
                    />
                )}
                sx={{
                    backgroundColor: '#fff',
                }}
            />
        </Box>
    );
};

export default SearchCollege;